import { axios } from "..";
import {
  ConditionFieldConfig,
  ConditionValueOption,
} from "../pages/SendEmail/types";

/** Ensures Person ID appears even if the functions host is running stale dist. */
const PERSON_ID_FIELD: ConditionFieldConfig = {
  id: "person-id",
  name: "Person ID",
  backendField: "id",
  inputType: "text",
  valueType: "id",
  operators: ["is-one-of", "is-not"],
  allowMultiValueForIs: true,
  placeholder: "Type person id and press Enter",
};

function mergePersonIdField(fields: ConditionFieldConfig[]): ConditionFieldConfig[] {
  if (fields.some((field) => field.id === PERSON_ID_FIELD.id)) {
    return fields;
  }
  return [PERSON_ID_FIELD, ...fields];
}

interface IFieldsResponse {
  fields: ConditionFieldConfig[];
}

interface IValuesResponse {
  fieldId: string;
  options: ConditionValueOption[];
}

export interface IMailAddressExistsResponse {
  email: string;
  exists: boolean;
  sources: string[];
}

interface IMailAddressExistsBatchResponse {
  results: IMailAddressExistsResponse[];
}

export const MAIL_ADDRESS_EXISTS_REQUEST_BATCH_SIZE = 100;

const normalizeMailAddressExistsResult = (
  email: string,
  payload?: Partial<IMailAddressExistsResponse>,
): IMailAddressExistsResponse => ({
  email: String(payload?.email || email),
  exists: Boolean(payload?.exists),
  sources: Array.isArray(payload?.sources) ? (payload?.sources ?? []) : [],
});

export const getEmailRecipientFilterFields = async (): Promise<
  ConditionFieldConfig[]
> => {
  try {
    const response = await axios.Get("./emailRecipientFilterOptions");
    const payload = response?.data as IFieldsResponse | undefined;
    const fields = payload?.fields;
    return mergePersonIdField(Array.isArray(fields) ? fields : []);
  } catch {
    return [];
  }
};

export const getEmailRecipientFilterValueOptions = async (
  fieldId: string,
): Promise<ConditionValueOption[]> => {
  try {
    const params = new URLSearchParams();
    params.append("action", "values");
    params.append("fieldId", fieldId);
    const response = await axios.Get(
      `./emailRecipientFilterOptions?${params.toString()}`,
    );
    const payload = response?.data as IValuesResponse | undefined;
    const options = payload?.options;
    return Array.isArray(options) ? options : [];
  } catch {
    return [];
  }
};

export const checkEmailRecipientMailAddressesExist = async (
  emails: string[],
): Promise<IMailAddressExistsResponse[]> => {
  const normalizedEmails = emails
    .map((email) => email.trim())
    .filter(Boolean)
    .filter(
      (email, index, collection) =>
        index ===
        collection.findIndex(
          (candidate) => candidate.toLowerCase() === email.toLowerCase(),
        ),
    );

  if (!normalizedEmails.length) {
    return [];
  }

  const results: IMailAddressExistsResponse[] = [];

  try {
    for (
      let offset = 0;
      offset < normalizedEmails.length;
      offset += MAIL_ADDRESS_EXISTS_REQUEST_BATCH_SIZE
    ) {
      const batch = normalizedEmails.slice(
        offset,
        offset + MAIL_ADDRESS_EXISTS_REQUEST_BATCH_SIZE,
      );
      const params = new URLSearchParams();
      params.append("action", "mail-address-exists-batch");
      params.append("emails", JSON.stringify(batch));
      const response = await axios.Get(
        `./emailRecipientFilterOptions?${params.toString()}`,
      );
      const payload = response?.data as
        | IMailAddressExistsBatchResponse
        | undefined;
      const batchResults = Array.isArray(payload?.results)
        ? (payload?.results ?? [])
        : [];

      batchResults.forEach((item, index) => {
        results.push(
          normalizeMailAddressExistsResult(batch[index] || item.email, item),
        );
      });
    }

    return results;
  } catch {
    return normalizedEmails.map((email) =>
      normalizeMailAddressExistsResult(email),
    );
  }
};

export const checkEmailRecipientMailAddressExists = async (
  email: string,
): Promise<IMailAddressExistsResponse> => {
  const [result] = await checkEmailRecipientMailAddressesExist([email]);
  return result || normalizeMailAddressExistsResult(email);
};
